import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { isPersonLegal } from '@dealstack/shared/lib/common/business/person.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { updatePersonLegal } from '@dealstack/frontend/app/services/Person.service.ts';
import {
	CLASSIFIERS_FORM_FIELDS,
	ClassifiersFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/types.ts';
import { ClassifiersFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/util/personClassifierShape.ts';

export const usePersonClassifierSection = () => {
	const {
		selectedEntityId,
		selectedEntityData,
		refetchSelectedEntity,
		isFetchingSelectedEntityData,
		refetchPersonsAndTags,
	} = useContext(CapManageControllerContext);

	const updateLegalPersonDetailsRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_PERSONID_LEGAL_PUT, { personId: selectedEntityId }],
		mutationFn: updatePersonLegal,
	});

	const classifiersForm = useForm<ClassifiersFormValues>({
		mode: 'all',
		resolver: zodResolver(ClassifiersFormShape),
	});

	useEffect(() => {
		if (!selectedEntityData || !isPersonLegal(selectedEntityData.type)) {
			return;
		}

		classifiersForm.reset({
			[CLASSIFIERS_FORM_FIELDS.display_name]: selectedEntityData.displayName,
			[CLASSIFIERS_FORM_FIELDS.tags]: selectedEntityData.tags ?? [],
		});
	}, [selectedEntityData]);

	const onFormSubmit = async (formValues: ClassifiersFormValues) => {
		const { display_name, tags } = formValues;

		return updateLegalPersonDetailsRequest.mutateAsync(
			{
				personId: selectedEntityId,
				updateArgs: {
					displayName: display_name || '',
					tags,
				},
			},
			{
				onSuccess: () => {
					void refetchSelectedEntity();
					void refetchPersonsAndTags();
				},
			}
		);
	};

	return {
		selectedEntityData,
		classifiersForm,
		onFormSubmit,
		isLoading: isFetchingSelectedEntityData || updateLegalPersonDetailsRequest.isPending,
		refetchPersonsAndTags,
	};
};
